import { useState, useCallback, useEffect, useRef } from 'react';
import { SortingState } from '@tanstack/react-table';
import request from 'lib/request';
import type {
  DcaScenariosQueryResponse,
  DcaScenariosQueryFacets,
} from 'types/api-responses';
import type { DcaScenariosFilterState, DcaScenarioRow } from './types';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const SORTABLE_ATTRIBUTES = new Set([
  'name',
  'notes',
  'area_count',
  'formation_count',
  'development_type',
  'segmentation',
  'outlier_detection',
  'early_time_on',
  'oil_configs_count',
  'gas_configs_count',
  'water_configs_count',
  'created_at',
  'created_by',
  'updated_at',
  'updated_by',
]);

function mapResultToRow(
  r: DcaScenariosQueryResponse['results'][0]
): DcaScenarioRow {
  return {
    id: r.id,
    name: r.name,
    notes: r.notes ?? null,
    area_count: r.areaCount,
    formation_count: r.formationCount,
    development_type: (r.developmentType ??
      '') as DcaScenarioRow['development_type'],
    segmentation: r.hasSegmentation ? 'Yes' : null,
    outlier_detection: r.hasOutlierDetection ? 'Yes' : null,
    early_time_on: r.hasEarlyTime,
    oil_configs_count: r.oilConfigCount,
    gas_configs_count: r.gasConfigCount,
    water_configs_count: r.waterConfigCount,
    created_at: r.createdAt,
    created_by: r.createdBy ?? null,
    updated_at: r.updatedAt,
    updated_by: r.updatedBy ?? null,
  };
}

function buildQueryPayload(
  filters: DcaScenariosFilterState,
  page: number,
  pageSize: number,
  searchText: string,
  sortAttribute: string | null,
  sortDirection: 'asc' | 'desc'
): Record<string, unknown> {
  const query: Record<string, unknown> = {
    page,
    pageSize,
  };
  if (searchText.trim()) query.text = searchText.trim();
  const effectiveSort =
    sortAttribute && SORTABLE_ATTRIBUTES.has(sortAttribute)
      ? sortAttribute
      : 'name';
  const effectiveDir =
    sortAttribute && SORTABLE_ATTRIBUTES.has(sortAttribute)
      ? sortDirection
      : 'asc';
  query.sortAttribute = effectiveSort;
  query.sortDirection = effectiveDir.toUpperCase();
  if (filters.createdBy) query.createdBy = filters.createdBy;
  if (filters.developmentType) {
    query.developmentType = filters.developmentType;
  }
  if (filters.createdDateStart || filters.createdDateEnd) {
    query.createdAt = {
      start: filters.createdDateStart || undefined,
      end: filters.createdDateEnd || undefined,
    };
  }
  if (filters.updatedDateStart || filters.updatedDateEnd) {
    query.updatedAt = {
      start: filters.updatedDateStart || undefined,
      end: filters.updatedDateEnd || undefined,
    };
  }
  if (filters.areaCountMin !== '' || filters.areaCountMax !== '') {
    query.areaCount = {
      min: filters.areaCountMin !== '' ? filters.areaCountMin : undefined,
      max: filters.areaCountMax !== '' ? filters.areaCountMax : undefined,
    };
  }
  if (filters.formationCountMin !== '' || filters.formationCountMax !== '') {
    query.formationCount = {
      min:
        filters.formationCountMin !== ''
          ? filters.formationCountMin
          : undefined,
      max:
        filters.formationCountMax !== ''
          ? filters.formationCountMax
          : undefined,
    };
  }
  if (filters.hasSegmentation) query.hasSegmentation = true;
  if (filters.hasOutlierDetection) query.hasOutlierDetection = true;
  if (filters.hasEarlyTime) query.hasEarlyTime = true;
  if (filters.hasOilConfigs) query.hasOilConfigs = true;
  if (filters.hasGasConfigs) query.hasGasConfigs = true;
  if (filters.hasWaterConfigs) query.hasWaterConfigs = true;
  return { query };
}

export const DCA_SCENARIOS_PAGE_SIZE = PAGE_SIZE;

export interface UseDcaScenariosListReturn {
  rows: DcaScenarioRow[];
  isLoading: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  totalCount: number;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  search: string;
  setSearch: (search: string) => void;
  setSort: (sorting: SortingState) => void;
  facets: DcaScenariosQueryFacets | null;
  initialSort:
    { sortColumn: string; sortDirection: 'asc' | 'desc' } | undefined;
}

export function useDcaScenariosList(
  filters: DcaScenariosFilterState
): UseDcaScenariosListReturn {
  const [rows, setRows] = useState<DcaScenarioRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [, setHasNextPage] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [facets, setFacets] = useState<DcaScenariosQueryFacets | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(PAGE_SIZE);

  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [sortColumn, setSortColumn] = useState<string | null>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const abortControllerRef = useRef<AbortController | null>(null);
  const isFetchingRef = useRef(false);

  const fetchPage = useCallback(
    async (page: number, append: boolean) => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      if (abortControllerRef.current) abortControllerRef.current.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      if (!append) setIsLoading(true);

      const payload = buildQueryPayload(
        filters,
        page,
        pageSize,
        debouncedSearch,
        sortColumn,
        sortDirection
      );

      try {
        const response = await request.post<
          DcaScenariosQueryResponse,
          { message?: string }
        >('/queries/reservoir/dca_scenarios', payload, controller);

        if (controller.signal.aborted) return;

        if (response.type === 'abort') return;
        if (response.type === 'fatal') {
          setError(response.message ?? 'Request failed');
          setRows(append ? (prev) => prev : []);
          setHasNextPage(false);
          setTotalCount(0);
          setIsLoading(false);
          isFetchingRef.current = false;
          return;
        }
        if (response.type === 'error') {
          setError(response.data?.message ?? 'Request failed');
          setRows(append ? (prev) => prev : []);
          setHasNextPage(false);
          setTotalCount(0);
          setIsLoading(false);
          isFetchingRef.current = false;
          return;
        }

        if (response.type !== 'success') return;
        const data = response.data;
        const newRows = (data.results ?? []).map(mapResultToRow);
        if (append) {
          setRows((prev) => [...prev, ...newRows]);
        } else {
          setRows(newRows);
        }
        setTotalCount(data.totalCount ?? 0);
        setHasNextPage(
          (data.page ?? 1) * (data.pageSize ?? pageSize) <
            (data.totalCount ?? 0)
        );
        setCurrentPage(page);
        if (data.facets) setFacets(data.facets);
        setError(null);
      } catch (_e) {
        if (controller.signal.aborted) return;
        setError('Failed to load DCA scenarios');
        if (!append) setRows([]);
        setHasNextPage(false);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    },
    [filters, pageSize, debouncedSearch, sortColumn, sortDirection]
  );

  useEffect(() => {
    setRows([]);
    setCurrentPage(1);
    setHasNextPage(false);
    fetchPage(1, false);

    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      isFetchingRef.current = false;
    };
  }, [fetchPage]);

  const setPage = useCallback(
    (page: number) => {
      if (page < 1) return;
      fetchPage(page, false);
    },
    [fetchPage]
  );

  const setPageSize = useCallback((newSize: number) => {
    setPageSizeState(newSize);
  }, []);

  const setSearch = useCallback((search: string) => {
    setSearchText(search);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const setSort = useCallback((sorting: SortingState) => {
    if (sorting.length === 0) {
      setSortColumn('name');
      setSortDirection('asc');
    } else {
      setSortColumn(sorting[0].id);
      setSortDirection(sorting[0].desc ? 'desc' : 'asc');
    }
  }, []);

  return {
    rows,
    isLoading,
    error,
    page: currentPage,
    pageSize,
    totalCount,
    setPage,
    setPageSize,
    search: searchText,
    setSearch,
    setSort,
    facets,
    initialSort: {
      sortColumn: sortColumn ?? 'name',
      sortDirection,
    },
  };
}
